import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, MessageCircle, Users, ArrowRight } from 'lucide-react';
import ContactForm from './ContactForm';

const ContactInfo = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <section className="py-20 -mt-16 relative z-10">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full md:w-1/2"
          >
            <ContactForm />
          </motion.div>
          
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full md:w-1/2"
          >
            <div className="mb-8">
              <span className="text-secondary uppercase tracking-wider font-semibold">Parliamo</span>
              <h2 className="text-3xl font-monomakh text-primary mt-2 mb-4">I Nostri Contatti</h2>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                Da anni ci occupiamo di soddisfare le esigenze dei nostri clienti. Se stai pensando di 
                acquistare, vendere o affittare un immobile, siamo qui per aiutarti con tutta la nostra 
                esperienza e professionalità.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.div 
                {...fadeIn}
                className="transform transition-all duration-300 hover:translate-y-[-5px] hover:shadow-md bg-white p-5 rounded-xl border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-primary text-white p-3 rounded-full h-fit">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div className="ml-5">
                    <h3 className="font-semibold text-primary text-lg">Telefono</h3>
                    <p className="text-gray-600">+39 327 8855115 (cellulare)</p>
                    <p className="text-gray-600">055 752139 (ufficio)</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                {...fadeIn} 
                transition={{ delay: 0.1, duration: 0.6 }}
                className="transform transition-all duration-300 hover:translate-y-[-5px] hover:shadow-md bg-white p-5 rounded-xl border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-primary text-white p-3 rounded-full h-fit">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div className="ml-5">
                    <h3 className="font-semibold text-primary text-lg">Email</h3>
                    <p className="text-gray-600">info@molinaroimmobiliare.it</p>
                    <p className="text-gray-600">vendite@molinaroimmobiliare.it</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                {...fadeIn} 
                transition={{ delay: 0.2, duration: 0.6 }}
                className="transform transition-all duration-300 hover:translate-y-[-5px] hover:shadow-md bg-white p-5 rounded-xl border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-primary text-white p-3 rounded-full h-fit">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div className="ml-5">
                    <h3 className="font-semibold text-primary text-lg">Indirizzo</h3>
                    <p className="text-gray-600">Via Pisana 327,</p>
                    <p className="text-gray-600">50018 Scandicci (FI)</p>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                {...fadeIn} 
                transition={{ delay: 0.3, duration: 0.6 }}
                className="transform transition-all duration-300 hover:translate-y-[-5px] hover:shadow-md bg-white p-5 rounded-xl border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-primary text-white p-3 rounded-full h-fit">
                    <Clock className="h-6 w-6" />
                  </div>
                  <div className="ml-5">
                    <h3 className="font-semibold text-primary text-lg">Orari</h3>
                    <p className="text-gray-600">Lun - Ven: 9:30 - 12:30 | 15:30 - 19:30</p>
                    <p className="text-gray-600">Sab: Chiuso</p>
                    <p className="text-gray-600">Dom: Chiuso</p>
                  </div>
                </div>
              </motion.div>
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-6 bg-primary/5 p-6 rounded-xl"
            >
              <h3 className="font-semibold text-primary text-lg mb-3">Restiamo in Contatto</h3>
              <p className="text-gray-600 mb-4">
                Seguici sui social media per rimanere aggiornato sulle nostre ultime novità e offerte immobiliari.
              </p>
              <div className="flex space-x-4">
                <a 
                  href="https://www.facebook.com/MolinaroImmobiliare" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary hover:bg-primary-dark text-white p-3 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"></path>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/molinaro_immobiliare" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary hover:bg-primary-dark text-white p-3 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"></path>
                  </svg>
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;